/**
 * Полоса выбора раздела «Планы» над таблицей: сводка отмеченного, «открыть»,
 * смена даты, QR-папка, WeChat, файлы, техкарты и меню CRM. Вынесено из
 * `pages/PlansPage.tsx` (нарезка 14.10.26).
 */

import { DownloadLink } from "@/components/DownloadLink";
import { PlanDownloadMenu } from "@/components/PlanDownloadMenu";
import { QrFolderMenu } from "@/components/QrFolderMenu";
import { RangeCalendar } from "@/components/RangeCalendar";
import { SmartButtons } from "@/components/SmartButtons";
import type { SmartKey } from "@/lib/crmSmartButtons";
import { nf } from "@/lib/format";
import { type BulkPlan, listFits, MAX_EXPORT_PLANS, MAX_PLAN_IDS, plural, techcardHref } from "@/lib/plans";
import { MAX_LIST } from "@/lib/productNav";
import type { PlanRow } from "@/lib/providers";

import { OpenMenu } from "./OpenMenu";

/** Меню CRM полосы выбора — те, что у таблиц партий в CRM: партии, строки
 *  плана, папки, оплаты, поставщики. Меню товара здесь нет: оно на «Товарах». */
const SMART_KEYS: SmartKey[] = [
  "mpStockSupplyPlanDateID",
  "mpStockSupplyPlanID",
  "barcodeQrDataID",
  "cryptoID",
  "supplySupplierID",
];

export function PlansPickBar({
  picked,
  setPicked,
  products,
  openHref,
  plansHref,
  dateOpen,
  setDateOpen,
  dateWhy,
  dateBusy,
  moveDate,
  qrWhy,
  qrBusy,
  pickedPlans,
  pickedInOrder,
  createQr,
  addToQr,
  sendWechat,
  wechatWhy,
  wechatBusy,
  bulk,
  total,
  pickAll,
  smartIds,
}: {
  picked: ReadonlySet<number>;
  setPicked: (next: ReadonlySet<number>) => void;
  /** Товары отмеченных партий в порядке таблицы. */
  products: number[];
  openHref: string;
  /** Отмеченные планы отдельным окном: `/plans?ids=…&status=all`. */
  plansHref: string;
  dateOpen: boolean;
  setDateOpen: (next: boolean | ((on: boolean) => boolean)) => void;
  dateWhy: string | null;
  dateBusy: boolean;
  moveDate: (day: string) => Promise<void>;
  qrWhy: string;
  qrBusy: boolean;
  pickedPlans: PlanRow[];
  pickedInOrder: PlanRow[];
  createQr: (target?: PlanRow[]) => Promise<void>;
  addToQr: (qrId: number) => Promise<void>;
  sendWechat: () => Promise<void>;
  wechatWhy: string;
  wechatBusy: boolean;
  /** Что сделает «QR + WeChat»: папки заведутся, уйдут, пропустятся. */
  bulk: BulkPlan;
  /** Партий в отборе — «отметить все». */
  total: number;
  pickAll: () => void;
  smartIds: Partial<Record<SmartKey, number[]>>;
}) {
  return (
    <>
      {/* ПОЛОСА ВЫБОРА — СТРОКОЙ НАД ТАБЛИЦЕЙ (решение владельца 25.09.26,
          спека …-plans-export-smart): действия и меню CRM стоят там, куда
          смотрят, отмечая партии. До 25.09 полоса жила в подвале: в тулбаре
          она переносилась на вторую строку, и таблица прыгала вниз на первом
          же чекбоксе. Прыжок снимает постоянная строка фиксированной высоты —
          она есть и без выбора, с подсказкой, так что появление действий
          ничего не сдвигает. Меню отсюда раскрываются ВНИЗ, поверх таблицы. */}
      <div
        data-annot="выбор партий"
        className={
          // Зазор 6 px и поля 8 px, а не 8 и 12: «открыть ▾» рамкой шире прежней
          // ссылки на ~30 px, и на 1280 px с открытым меню слева полоса уходила
          // за край `main` на 21 px (замер 04.11.26). Девять зазоров и поля
          // возвращают 26 px, узкая кнопка — ещё 8.
          "flex h-9 flex-none items-center gap-1.5 whitespace-nowrap border border-b-0 border-border px-2 text-[12.5px] " +
          (picked.size > 0 ? "bg-accent-dim" : "bg-bg-subtle")
        }
      >
        {picked.size === 0 ? (
          <>
            <span className="text-fg-faint">отметьте партии — появятся действия и меню CRM</span>
            {/* Только в пустой полосе: в полной на 1280 px второй ссылке нет
                места — с «QR + WeChat · N» последнее меню CRM уезжало за край
                на 36 px (замер 23.10.26). Добрать до всех — «снять» и сюда. */}
            {total > 0 ? (
              <button
                type="button"
                onClick={pickAll}
                title="отметить все партии отбора"
                className="font-medium text-accent-text hover:underline"
              >
                отметить все · {nf.format(total)}
              </button>
            ) : null}
          </>
        ) : (
          <>
            {/* Без слова «выбрано»: отмеченные чекбоксы и так говорят, что
                это выбор, а пять меню CRM на 1280 px впритык. Сводка —
                единственное, что сжимается (многоточием, полный текст в
                подсказке): при сотне партий счётчики меню шире, и без этого
                последнее меню уехало бы за край `main`. */}
            <span
              title={`выбрано ${picked.size} ${plural(picked.size, ["партия", "партии", "партий"])} · ${products.length} ${plural(products.length, ["товар", "товара", "товаров"])}`}
              className="min-w-0 truncate text-fg-muted"
            >
              <b className="tnum font-medium text-fg">{nf.format(picked.size)}</b>{" "}
              {plural(picked.size, ["партия", "партии", "партий"])} ·{" "}
              <b className="tnum font-medium text-fg">{nf.format(products.length)}</b>{" "}
              {plural(products.length, ["товар", "товара", "товаров"])}
            </span>
            <OpenMenu
              cardHref={listFits(products) ? openHref : null}
              cardWhy={`не больше ${MAX_LIST} товаров за раз: список едет в адресе`}
              plansHref={picked.size <= MAX_PLAN_IDS ? plansHref : null}
              plansWhy={`не больше ${MAX_PLAN_IDS} партий за раз: список едет в адресе`}
            />
            <div className="relative">
              <button
                type="button"
                onClick={() => setDateOpen((on) => !on)}
                disabled={Boolean(dateWhy) || dateBusy}
                title={dateWhy || "сдвинуть плановую дату отмеченных партий"}
                className="font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-faint disabled:no-underline"
              >
                {dateBusy ? "переношу…" : "сменить дату"}
              </button>
              {dateOpen ? (
                <div className="absolute left-0 top-9 z-30 w-[260px] rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg">
                  <RangeCalendar
                    from=""
                    to=""
                    single
                    onPick={(day) => void moveDate(day)}
                  />
                </div>
              ) : null}
            </div>
            <QrFolderMenu
              why={qrWhy}
              busy={qrBusy}
              supplierId={pickedPlans[0]?.supplier_id ?? null}
              onNew={() => void createQr()}
              onExisting={(qrId) => void addToQr(qrId)}
            />
            {/* «QR + WeChat» (просьба владельца 23.10.26): есть группы без
                папки — сначала папки им, потом все заказы в очередь. Число —
                заказов, которые уйдут; пропущенные группы в него не входят. */}
            <button
              type="button"
              onClick={() => void sendWechat()}
              disabled={Boolean(wechatWhy) || wechatBusy || qrBusy}
              title={
                wechatWhy ||
                (bulk.create.length > 0
                  ? "завести QR-папку каждой группе без папки и отправить все заказы фабрикам в WeChat: QR, Excel, текст"
                  : "отправить заказы отмеченных QR-папок фабрикам в WeChat: QR, Excel, текст")
              }
              data-annot="WeChat отмеченных"
              className="font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-faint disabled:no-underline"
            >
              {wechatBusy
                ? "отправляю…"
                : bulk.create.length > 0
                  ? `QR + WeChat · ${bulk.create.length + bulk.send.length}`
                  : `в WeChat · ${bulk.send.length}`}
            </button>
            <PlanDownloadMenu
              ids={pickedInOrder.map((plan) => plan.id)}
              disabled={
                picked.size > MAX_EXPORT_PLANS
                  ? `не больше ${MAX_EXPORT_PLANS} партий за раз`
                  : undefined
              }
            />
            {/* Техкарты отмеченных архивом (разметка 25.09.26 15:28 п.3). Ни у одной
                нет — ссылки нет: 404 вместо файла закупщику ничего не скажет. */}
            {pickedInOrder.some((plan) => plan.has_card) && picked.size <= MAX_EXPORT_PLANS ? (
              <DownloadLink
                href={techcardHref({ ids: pickedInOrder.map((plan) => plan.id) })}
                title="техкарты отмеченных партий архивом, с подписью, как в CRM"
                data-annot="техкарты отмеченных"
                className="font-medium text-accent-text hover:underline"
              >
                техкарты
              </DownloadLink>
            ) : null}
            <button
              type="button"
              onClick={() => setPicked(new Set())}
              className="text-fg-muted hover:text-fg"
            >
              снять
            </button>
            <span className="h-4 w-px flex-none bg-border" />
            {/* Без переноса: у `SmartButtons` свой `flex-wrap`, и в строке
                фиксированной высоты второй ряд меню ушёл бы под шапку таблицы. */}
            <div className="flex-none [&>div]:flex-nowrap">
              <SmartButtons ids={smartIds} keys={SMART_KEYS} down caret={false} showEmpty />
            </div>
          </>
        )}
      </div>
    </>
  );
}
